import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload, FileText, Trash2, Loader2, Sparkles, Download, X, Plus,
  Building2, BookOpen, Tag, ChevronRight, MessageSquareText, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";

import Markdown from "@/components/Markdown";
import { api, API_BASE, formatApiError } from "@/lib/api";

const DOC_TYPES = ["Brochure", "Policy wording", "Endorsement", "Renewal notice", "Claim form", "Other"];
const CATEGORIES = ["Health", "Life", "Term", "Motor", "Travel", "Home", "Other"];

export default function Documents() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/documents");
      setDocs(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    (async () => { await refresh(); setLoading(false); })();
    // Poll for summary completion every 5s while there's any pending doc
    const id = setInterval(async () => {
      const hasPending = docs.some((d) => d.summary_status === "pending");
      if (hasPending) await refresh();
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const onDelete = async (docId) => {
    try {
      await api.delete(`/documents/${docId}`);
      toast.success("Document deleted");
      refresh();
      if (viewDoc?.document_id === docId) setViewDoc(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openView = async (doc) => {
    try {
      const { data } = await api.get(`/documents/${doc.document_id}`);
      setViewDoc(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const analyzeInChat = (doc) => {
    const question = `Please summarize and explain the key points of "${doc.policy_name}"${doc.insurer_name ? ` by ${doc.insurer_name}` : ""}. Highlight coverage, waiting periods, exclusions, and notable sub-limits.`;
    navigate(`/chat?prompt=${encodeURIComponent(question)}&doc=${doc.document_id}`);
  };

  return (
    <AppShell
      title="Policy documents"
      eyebrow="Library"
      action={
        <div className="flex items-center gap-2">
          <Button data-testid="docs-refresh-btn" onClick={refresh} variant="ghost" size="icon" className="rounded-full">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button data-testid="docs-upload-btn" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-9">
                <Upload className="w-4 h-4 mr-1.5" /> Upload PDF
              </Button>
            </DialogTrigger>
            <UploadDialog onUploaded={() => { refresh(); setUploadOpen(false); }} />
          </Dialog>
        </div>
      }
    >
      <div data-testid="documents-page" className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
        {loading ? (
          <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : docs.length === 0 ? (
          <EmptyState onUpload={() => setUploadOpen(true)} />
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {docs.map((d) => (
              <DocCard key={d.document_id} doc={d} onView={() => openView(d)} onDelete={() => onDelete(d.document_id)} onAnalyze={() => analyzeInChat(d)} />
            ))}
          </div>
        )}
      </div>

      <Sheet open={!!viewDoc} onOpenChange={(o) => !o && setViewDoc(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          {viewDoc && (
            <>
              <SheetHeader className="space-y-1">
                <SheetTitle className="font-display text-2xl tracking-tight">{viewDoc.policy_name}</SheetTitle>
                <p className="text-sm text-muted-foreground">{viewDoc.insurer_name || "Unknown insurer"} · {viewDoc.category} · {viewDoc.document_type}</p>
              </SheetHeader>

              <div className="mt-6 flex gap-2 flex-wrap">
                <Button
                  data-testid={`doc-analyze-btn-${viewDoc.document_id}`}
                  onClick={() => analyzeInChat(viewDoc)}
                  className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-9"
                >
                  <MessageSquareText className="w-3.5 h-3.5 mr-1.5" /> Discuss in chat
                </Button>
                <a
                  data-testid={`doc-download-link-${viewDoc.document_id}`}
                  href={`${API_BASE}/documents/${viewDoc.document_id}/download`}
                  target="_blank" rel="noreferrer"
                >
                  <Button variant="outline" className="rounded-full h-9"><Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF</Button>
                </a>
              </div>

              <div className="mt-8">
                <h3 className="font-display text-base font-semibold mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-accent" /> AI summary
                </h3>
                {viewDoc.summary_status === "pending" ? (
                  <div className="bg-muted/40 rounded-2xl p-6 text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Generating summary — this can take a minute. The page auto-refreshes.
                  </div>
                ) : viewDoc.summary_status === "failed" ? (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-2xl p-6 text-sm text-destructive">
                    Summary generation failed. Try re-uploading the document.
                  </div>
                ) : viewDoc.summary_status === "skipped" ? (
                  <div className="bg-muted/40 rounded-2xl p-6 text-sm text-muted-foreground italic">
                    This PDF appears to be a scanned image with no extractable text. OCR support is coming soon.
                  </div>
                ) : viewDoc.summary ? (
                  <div className="bg-card border border-border rounded-2xl p-5">
                    <Markdown text={viewDoc.summary} />
                  </div>
                ) : (
                  <div className="bg-muted/40 rounded-2xl p-6 text-sm text-muted-foreground italic">
                    Couldn&apos;t extract enough text from this PDF to summarize. It may be a scanned image.
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </AppShell>
  );
}

function EmptyState({ onUpload }) {
  return (
    <div className="text-center py-16">
      <div className="w-14 h-14 rounded-2xl bg-accent/10 text-accent flex items-center justify-center mx-auto mb-5">
        <FileText className="w-6 h-6" />
      </div>
      <h2 className="font-display text-2xl font-semibold tracking-tight">Your policy library is empty.</h2>
      <p className="mt-2 text-muted-foreground max-w-md mx-auto text-sm">
        Upload an insurance brochure or policy PDF. InsureGenie reads it, summarizes it,
        and makes it available for any conversation.
      </p>
      <Button data-testid="docs-empty-upload-btn" onClick={onUpload} className="mt-6 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
        <Upload className="w-4 h-4 mr-1.5" /> Upload your first document
      </Button>
    </div>
  );
}

function DocCard({ doc, onView, onDelete, onAnalyze }) {
  const date = new Date(doc.upload_date || doc.created_at);
  return (
    <div
      data-testid={`doc-card-${doc.document_id}`}
      className="bg-card border border-border rounded-2xl p-5 hover:border-accent/40 transition-colors cursor-pointer group"
      onClick={onView}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center flex-shrink-0 group-hover:bg-accent/15 group-hover:text-accent transition-colors">
          <FileText className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-base tracking-tight truncate">{doc.policy_name}</div>
          <div className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-0.5">
            <Building2 className="w-3 h-3" /> {doc.insurer_name || "Unknown insurer"}
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <Badge variant="secondary" className="text-[10px] rounded-full px-2 py-0">{doc.category}</Badge>
            <Badge variant="secondary" className="text-[10px] rounded-full px-2 py-0">{doc.document_type}</Badge>
            <Badge variant="secondary" className="text-[10px] rounded-full px-2 py-0">v{doc.version}</Badge>
          </div>
          <div className="mt-3 text-[11px] text-muted-foreground flex items-center justify-between">
            <span>{date.toLocaleDateString()}</span>
            <span className="inline-flex items-center gap-1">
              {doc.summary_status === "ready" && <><Sparkles className="w-3 h-3 text-accent" /> Summary ready</>}
              {doc.summary_status === "pending" && <><Loader2 className="w-3 h-3 animate-spin" /> Summarizing…</>}
              {doc.summary_status === "failed" && <span className="text-destructive">Summary failed</span>}
              {doc.summary_status === "skipped" && <span className="text-muted-foreground/70">No text</span>}
            </span>
          </div>
          {(doc.embedding_status === "ready" || doc.ocr_used) && (
            <div className="mt-2 flex flex-wrap gap-1">
              {doc.embedding_status === "ready" && doc.vector_chunk_count > 0 && (
                <Badge data-testid={`doc-rag-${doc.document_id}`} variant="secondary" className="text-[10px] rounded-full px-2 py-0 bg-accent/10 text-accent border border-accent/20">
                  RAG · {doc.vector_chunk_count} chunk{doc.vector_chunk_count > 1 ? "s" : ""}
                </Badge>
              )}
              {doc.ocr_used && (
                <Badge data-testid={`doc-ocr-${doc.document_id}`} variant="secondary" className="text-[10px] rounded-full px-2 py-0 bg-secondary/10 text-secondary border border-secondary/20">
                  OCR
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
        <Button data-testid={`doc-card-discuss-${doc.document_id}`} onClick={onAnalyze} variant="ghost" size="sm" className="text-xs h-8">
          <MessageSquareText className="w-3 h-3 mr-1.5" /> Discuss
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button data-testid={`doc-card-delete-${doc.document_id}`} variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this document?</AlertDialogTitle>
              <AlertDialogDescription>This will remove the PDF and its summary from your library.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onDelete} className="bg-destructive hover:bg-destructive/90 text-destructive-foreground">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function UploadDialog({ onUploaded }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ insurer_name: "", policy_name: "", document_type: "Brochure", category: "Health", version: "v1" });
  const [uploading, setUploading] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are supported.");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error("File is too large (max 15 MB).");
      return;
    }
    setFile(f);
    if (!meta.policy_name) {
      setMeta((m) => ({ ...m, policy_name: f.name.replace(/\.pdf$/i, "") }));
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a PDF first."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded. Summary is being generated.");
      setFile(null);
      setMeta({ insurer_name: "", policy_name: "", document_type: "Brochure", category: "Health", version: "v1" });
      onUploaded?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle className="font-display text-xl">Upload policy PDF</DialogTitle>
        <DialogDescription>
          Brochures, policy wordings, endorsements — anything insurance. Max 15 MB.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div
          data-testid="upload-dropzone"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
          className="border-2 border-dashed border-border rounded-2xl p-6 text-center cursor-pointer hover:border-accent/50 transition-colors"
        >
          <input ref={fileRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="upload-file-input" />
          {file ? (
            <div className="flex items-center justify-center gap-2">
              <FileText className="w-5 h-5 text-accent" />
              <span className="text-sm font-medium truncate max-w-[300px]">{file.name}</span>
              <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-muted-foreground hover:text-destructive ml-1"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <>
              <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm font-medium">Click to choose or drop a PDF here</p>
              <p className="text-[11px] text-muted-foreground mt-1">PDF · up to 15 MB</p>
            </>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Insurer name</Label>
            <Input data-testid="upload-insurer-input" value={meta.insurer_name} onChange={(e) => setMeta({ ...meta, insurer_name: e.target.value })} placeholder="e.g. Star Health" className="mt-1.5 h-10 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Policy name</Label>
            <Input data-testid="upload-policy-input" value={meta.policy_name} onChange={(e) => setMeta({ ...meta, policy_name: e.target.value })} placeholder="e.g. Comprehensive" className="mt-1.5 h-10 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Category</Label>
            <Select value={meta.category} onValueChange={(v) => setMeta({ ...meta, category: v })}>
              <SelectTrigger data-testid="upload-category" className="mt-1.5 h-10 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Document type</Label>
            <Select value={meta.document_type} onValueChange={(v) => setMeta({ ...meta, document_type: v })}>
              <SelectTrigger data-testid="upload-doctype" className="mt-1.5 h-10 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Version</Label>
            <Input data-testid="upload-version-input" value={meta.version} onChange={(e) => setMeta({ ...meta, version: e.target.value })} placeholder="e.g. v1 or 2025-Q1" className="mt-1.5 h-10 rounded-xl" />
          </div>
        </div>

        <DialogFooter>
          <Button
            data-testid="upload-submit-btn"
            type="submit"
            disabled={!file || uploading}
            className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> Upload & summarize</>}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
